import * as React from "react";
import {MaintenanceRegisteredEntityType, UserSearch } from "@app/maintenance";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { JSX } from "react";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { Client } from "@app/client/client";
import { MaintenanceAdminViewModel } from "@app/maintenance/maintenanceAdminViewModel";
import { MaintenancePropertyCard } from "@app/maintenance/MaintenancePropertyCard";
import { MaintenanceRegisteredEntity } from "@app/maintenance/maintenanceRegisteredEntity";
import MaintenanceService from "@app/maintenance/maintenanceService";

interface MaintenanceAdminState {
  // the model that handles bulk editing of registered entities
  bulkEditRegisteredEntity: MaintenanceRegisteredEntity;
  // the available clients for the user search
  clients: Client[];
  // determines the "update all" button's disabled state
  disableUpdateAllButton: boolean;
  // obtaining maintenance registered entities
  loading: boolean;
  // the role entities whose properties are manipulated
  roles: MaintenanceRegisteredEntity[];
  // the checkbox that selectes or deselects all role registered entities
  selectAllRoles: boolean;
  // the checkbox that selectes or deselects all user registered entities
  selectAllUsers: boolean;
  // the registered entities that have been selected for bulk editing
  selectedRegisteredEntities: MaintenanceRegisteredEntity[];
  // the role entities whose properties are currently stored server-side
  storedRoles: MaintenanceRegisteredEntity[];
  // the role entities whose properties are currently stored server-side
  storedUsers: MaintenanceRegisteredEntity[];
  // the user entities whose properties are manipulated
  users: MaintenanceRegisteredEntity[];
}

interface MaintenanceTableData {
  [wafflesKeyColumnName]: string;
  maintenanceAlert: JSX.Element;
  maintenanceCountdown: JSX.Element;
  maintenancePage: JSX.Element;
  registeredEntity: JSX.Element;
}

const columnConfig: ColumnConfig = {
  maintenanceAlert: {
    displayText: "Maintenance Alert",
    noSort: true,
    testId: "maintenance-alert",
  },
  maintenanceCountdown: {
    displayText: "Maintenance Countdown",
    noSort: true,
    testId: "maintenance-countdown",
  },
  maintenancePage: {
    displayText: "Maintenance Page",
    noSort: true,
    testId: "maintenance-page",
  },
  registeredEntity: {
    displayText: "Registered Entity",
    noSort: true,
    testId: "registered-entity",
  }
};

class MaintenanceAdmin extends React.Component<unknown, MaintenanceAdminState> {
  private readonly maintenanceService = new MaintenanceService();

  constructor(props: unknown) {
    super(props);
    this.state = {
      bulkEditRegisteredEntity: this.createBulkEditRegisteredEntityModel(),
      clients: [],
      disableUpdateAllButton: true,
      loading: true,
      roles: [],
      selectAllRoles: false,
      selectAllUsers: false,
      selectedRegisteredEntities: [],
      storedRoles: [],
      storedUsers: [],
      users: [],
    };
  }

  public async componentDidMount() {
    await this.getMaintenanceRegisteredEntities();
  }

  public componentDidUpdate() {
    // handling whether the "update all" button should be enabled or disabled via comparison
    if (JSON.stringify(this.state.roles) !== JSON.stringify(this.state.storedRoles) || JSON.stringify(this.state.users) !== JSON.stringify(this.state.storedUsers)) {
      if (this.state.disableUpdateAllButton) {
        this.setState({
          disableUpdateAllButton: false,
        });
      }
    } else {
      if (!this.state.disableUpdateAllButton) {
        this.setState({
          disableUpdateAllButton: true,
        });
      }
    }
  }

  /********** conducts a search for users based on a selected client or an input name **********/
  public getUsers = async (value: string, type: "name" | "client"): Promise<void> => {
    let storedUsers: MaintenanceRegisteredEntity[];

    if (type === "name") {
      storedUsers = await this.maintenanceService.getMaintenanceUsersByName(value);
    } else {
      storedUsers = await this.maintenanceService.getMaintenanceUsersByClient(value);
    }

    const users: MaintenanceRegisteredEntity[] = JSON.parse(JSON.stringify(storedUsers));

    // remove any selected users
    const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities.filter((x) => x.registeredEntityType !== MaintenanceRegisteredEntityType.User);

    this.setState({
      selectedRegisteredEntities,
      storedUsers,
      users,
    });
  }

  /********** toggles whether a registered entity's maintenance alert should be displayed **********/
  public onMaintenanceAlertToggle = (registeredEntity: MaintenanceRegisteredEntity): void => {
    if (registeredEntity.registeredEntity === "BULK-EDIT") {
      // toggling the bulk edit model
      const bulkEditRegisteredEntity: MaintenanceRegisteredEntity = this.state.bulkEditRegisteredEntity;
      const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities;
      bulkEditRegisteredEntity.showMaintenanceAlert = !bulkEditRegisteredEntity.showMaintenanceAlert;

      for (const selectedRegisteredEntity of selectedRegisteredEntities) {
        // changing each selected registered entity's maintenance alert status
        // to reflect that of the bulk edit model
        selectedRegisteredEntity.showMaintenanceAlert = bulkEditRegisteredEntity.showMaintenanceAlert;
      }

      this.setState({
        bulkEditRegisteredEntity,
        selectedRegisteredEntities,
      });
    } else {
      // toggling a normal registered entity model
      if (registeredEntity.registeredEntityType === MaintenanceRegisteredEntityType.Role) {
        // role
        const roles: MaintenanceRegisteredEntity[] = this.state.roles;
        const entity: MaintenanceRegisteredEntity = roles.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.showMaintenanceAlert = !entity.showMaintenanceAlert;
        }

        this.setState({
          roles,
        });
      } else {
        // user
        const users: MaintenanceRegisteredEntity[] = this.state.users;
        const entity: MaintenanceRegisteredEntity = users.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.showMaintenanceAlert = !entity.showMaintenanceAlert;
        }

        this.setState({
          users,
        });
      }
    }
  }

  /********** handles the date and time of a registered entity's maintenance alert countdown **********/
  public onMaintenanceAlertCountdownChange = (e: React.ChangeEvent<HTMLInputElement>, registeredEntity: MaintenanceRegisteredEntity): void => {
    if (registeredEntity.registeredEntity === "BULK-EDIT") {
      // toggling the bulk edit model
      const bulkEditRegisteredEntity: MaintenanceRegisteredEntity = this.state.bulkEditRegisteredEntity;
      const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities;
      bulkEditRegisteredEntity.maintenanceTime = e.target.value;

      for (const selectedRegisteredEntity of this.state.selectedRegisteredEntities) {
        // changing each selected registered entity's maintenance alert status
        // to reflect that of the bulk edit model
        selectedRegisteredEntity.maintenanceTime = bulkEditRegisteredEntity.maintenanceTime;
      }

      this.setState({
        bulkEditRegisteredEntity,
        selectedRegisteredEntities,
      });
    } else {
      // toggling a normal registered entity model
      if (registeredEntity.registeredEntityType === MaintenanceRegisteredEntityType.Role) {
        // role
        const roles: MaintenanceRegisteredEntity[] = this.state.roles;
        const entity: MaintenanceRegisteredEntity = roles.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.maintenanceTime = e.target.value;
        }

        this.setState({
          roles,
        });
      } else {
        // user
        const users: MaintenanceRegisteredEntity[] = this.state.users;
        const entity: MaintenanceRegisteredEntity = users.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.maintenanceTime = e.target.value;
        }

        this.setState({
          users,
        });
      }
    }
  }

  /********** toggles whether a registered entity's maintenance page should be displayed **********/
  public onMaintenancePageToggle = (registeredEntity: MaintenanceRegisteredEntity): void => {
    if (registeredEntity.registeredEntity === "BULK-EDIT") {
      // toggling the bulk edit model
      const bulkEditRegisteredEntity: MaintenanceRegisteredEntity = this.state.bulkEditRegisteredEntity;
      const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities;
      bulkEditRegisteredEntity.showMaintenancePage = !bulkEditRegisteredEntity.showMaintenancePage;

      for (const selectedRegisteredEntity of selectedRegisteredEntities) {
        // changing each selected registered entity's maintenance page status
        // to reflect that of the bulk edit model
        selectedRegisteredEntity.showMaintenancePage = bulkEditRegisteredEntity.showMaintenancePage;
      }

      this.setState({
        bulkEditRegisteredEntity,
        selectedRegisteredEntities,
      });
    } else {
      // toggling a normal registered entity model
      if (registeredEntity.registeredEntityType === MaintenanceRegisteredEntityType.Role) {
        // role
        const roles: MaintenanceRegisteredEntity[] = this.state.roles;
        const entity: MaintenanceRegisteredEntity = roles.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.showMaintenancePage = !entity.showMaintenancePage;
        }

        this.setState({
          roles,
        });
      } else {
        // user
        const users: MaintenanceRegisteredEntity[] = this.state.users;
        const entity: MaintenanceRegisteredEntity = users.find((r) => r.registeredEntity === registeredEntity.registeredEntity);

        if (entity) {
          entity.showMaintenancePage = !entity.showMaintenancePage;
        }

        this.setState({
          users,
        });
      }
    }
  }

  /********** toggles whether all roles are selected or not **********/
  public onSelectAllRolesToggle = (): void => {
    const selectAllRoles = !this.state.selectAllRoles;

    if (selectAllRoles) {
      // all roles are selected
      const selectedUsers = this.state.selectedRegisteredEntities.filter((sre) => sre.registeredEntityType === MaintenanceRegisteredEntityType.User);
      this.setState({
        selectAllRoles: !this.state.selectAllRoles,
        selectedRegisteredEntities: selectedUsers.concat(this.state.roles)
      });
    } else {
      // no roles are selected
      const selectedRegisteredEntities = this.state.selectedRegisteredEntities.filter((x) => x.registeredEntityType !== MaintenanceRegisteredEntityType.Role);

      this.setState({
        selectAllRoles: !this.state.selectAllRoles,
        selectedRegisteredEntities,
      });
    }
  }

  /********** toggles whether all users are selected or not **********/
  public onSelectAllUsersToggle = (): void => {
    const selectAllUsers = !this.state.selectAllUsers;

    if (selectAllUsers) {
      // all users are selected
      const selectedRoles = this.state.selectedRegisteredEntities.filter((sre) => sre.registeredEntityType === MaintenanceRegisteredEntityType.Role);
      this.setState({
        selectAllUsers: !this.state.selectAllUsers,
        selectedRegisteredEntities: selectedRoles.concat(this.state.users),
      });
    } else {
      // no users are selected
      const selectedRegisteredEntities = this.state.selectedRegisteredEntities.filter((x) => x.registeredEntityType !== MaintenanceRegisteredEntityType.User);

      this.setState({
        selectAllUsers: !this.state.selectAllUsers,
        selectedRegisteredEntities,
      });
    }
  }

  /********** toggles whether an individual maintenance registered entity card is selected or not **********/
  public onSelectRegisteredEntityToggle = (registeredEntity: MaintenanceRegisteredEntity): void => {
    // check to see if the registered entity we are toggling exists
    // in the array of selected registered entities
    const selected: MaintenanceRegisteredEntity = this.state.selectedRegisteredEntities.find((r) => r === registeredEntity);

    if (selected) {
      // remove from selected registered entities array
      const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities.filter((x) => x !== selected);

      if (registeredEntity.registeredEntityType === MaintenanceRegisteredEntityType.Role) {
        // role
        if (this.state.selectAllRoles) {
          // if the select all roles checkbox is selected,
          // deselect it, as not all checkboxes will be selected anymore
          this.setState({
            selectAllRoles: false,
          });
        }
      } else {
        // user
        if (this.state.selectAllUsers) {
          // if the select all users checkbox is selected,
          // deselect it, as not all checkboxes will be selected anymore
          this.setState({
            selectAllUsers: false,
          });
        }
      }

      this.setState({
        selectedRegisteredEntities,
      });
    } else {
      // add to selected registered entities array
      const selectedRegisteredEntities: MaintenanceRegisteredEntity[] = this.state.selectedRegisteredEntities.concat(registeredEntity);

      if (registeredEntity.registeredEntityType === MaintenanceRegisteredEntityType.Role) {
        // role
        if (!this.state.selectAllRoles) {
          const selectedRoles: MaintenanceRegisteredEntity[] = selectedRegisteredEntities.filter((x) => x.registeredEntityType === MaintenanceRegisteredEntityType.Role);

          if (selectedRoles.length === this.state.roles.length) {
            // if the select all registered entities checkbox is not selected,
            // and all role registered entities have been selected, select it
            this.setState({
              selectAllRoles: true,
            });
          }
        }
      } else {
        // user
        if (!this.state.selectAllUsers) {
          const selectedUsers: MaintenanceRegisteredEntity[] = selectedRegisteredEntities.filter((x) => x.registeredEntityType === MaintenanceRegisteredEntityType.User);

          if (selectedUsers.length === this.state.users.length) {
            // if the select all registered entities checkbox is not selected,
            // and all role registered entities have been selected, select it
            this.setState({
              selectAllUsers: true,
            });
          }
        }
      }

      this.setState({
        selectedRegisteredEntities,
      });
    }
  }

  /********** updates all of the registered entities' currently-selected options **********/
  public onUpdateAllClick = async (): Promise<void> => {
    const registeredEntitiesToUpdate: MaintenanceRegisteredEntity[] = [];

    for (const role of this.state.roles) {
      const storedRegisteredEntity = this.state.storedRoles.find((s) => s.registeredEntity === role.registeredEntity);

      if (storedRegisteredEntity && JSON.stringify(storedRegisteredEntity) !== JSON.stringify(role)) {
        registeredEntitiesToUpdate.push(role);
      }
    }

    for (const user of this.state.users) {
      const storedRegisteredEntity = this.state.storedUsers.find((s) => s.registeredEntity === user.registeredEntity);

      if (storedRegisteredEntity && JSON.stringify(storedRegisteredEntity) !== JSON.stringify(user)) {
        registeredEntitiesToUpdate.push(user);
      }
    }

    await this.maintenanceService.updateMaintenanceRegisteredEntities(registeredEntitiesToUpdate);

    const storedRoles: MaintenanceRegisteredEntity[] = this.state.roles;
    const roles: MaintenanceRegisteredEntity[] = JSON.parse(JSON.stringify(storedRoles));
    const storedUsers: MaintenanceRegisteredEntity[] = this.state.users;
    const users: MaintenanceRegisteredEntity[] = JSON.parse(JSON.stringify(storedUsers));

    this.setState({
      roles,
      selectAllRoles: false,
      selectAllUsers: false,
      selectedRegisteredEntities: [],
      storedRoles,
      storedUsers,
      users,
    });
  }

  public render() {
    const pageTitle = "Maintenance Admin";

    return (
      <Page documentTitle={pageTitle}>
        <PageInner>
          <PageHeader iconName={FontAwesome.Cog} title={pageTitle} />
          {this.state.loading ?
            <PageSection className="mt-3">
              <div className="row">
                <div className="col-12">
                  <div className="text-center lead">
                    <i className="fa fa-spinner" /> Loading...
                  </div>
                </div>
              </div>
            </PageSection> :
            <PageSection className="mt-3">
              {this.state.selectedRegisteredEntities.length > 0 &&
                <div className="row">
                  <div className="col-12">
                    <legend className="mb-0">
                      Bulk-Edit Selected Registered Entities
                    </legend>
                    <div className="mb-3">
                      {this.getBulkEditRegisteredEntities()}
                    </div>
                  </div>

                  <MaintenancePropertyCard
                    faIcon="wrench"
                    isChecked={this.state.bulkEditRegisteredEntity.showMaintenancePage}
                    onMaintenancePropertyToggle={this.onMaintenancePageToggle}
                    registeredEntity={this.state.bulkEditRegisteredEntity}
                    subtitle="Displays a maintenance page to the registered entity"
                    title="Maintenance Page"
                  >
                    <div>
                      <i className={"me-1 fa fa-fw fa-" + (this.state.bulkEditRegisteredEntity.showMaintenancePage ? "check-square" : "square-o")} />
                      Show Maintenance Page
                    </div>
                  </MaintenancePropertyCard>

                  <MaintenancePropertyCard
                    faIcon="warning"
                    isChecked={this.state.bulkEditRegisteredEntity.showMaintenanceAlert}
                    onMaintenancePropertyToggle={this.onMaintenanceAlertToggle}
                    registeredEntity={this.state.bulkEditRegisteredEntity}
                    subtitle="Displays an alert informing the registered entity of upcoming maintenance"
                    title="Maintenance Alert"
                  >
                    <div>
                      <i className={"me-1 fa fa-fw fa-" + (this.state.bulkEditRegisteredEntity.showMaintenanceAlert ? "check-square" : "square-o")} />
                      Show Maintenance Alert
                    </div>
                  </MaintenancePropertyCard>

                  <MaintenancePropertyCard
                    faIcon="calendar"
                    isChecked={(this.state.bulkEditRegisteredEntity.maintenanceTime === "") ? false : true}
                    registeredEntity={this.state.bulkEditRegisteredEntity}
                    subtitle="Adds a countdown to the maintenance alert"
                    title="Maintenance Countdown"
                  >
                    <input
                      className="form-control cursor-pointer"
                      onChange={(e) => this.onMaintenanceAlertCountdownChange(e, this.state.bulkEditRegisteredEntity)}
                      type="datetime-local"
                      value={this.state.bulkEditRegisteredEntity.maintenanceTime}
                    />
                  </MaintenancePropertyCard>
                </div>
              }
              <div className="row">
                {this.state.selectedRegisteredEntities.length > 0 &&
                  <div className="col-12">
                    <legend className="mb-0">
                      Edit Registered Entities Individually
                    </legend>
                  </div>
                }

                <div className="col-12">
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a className="nav-link show active" data-bs-toggle="tab" href="#roles">Roles</a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" data-bs-toggle="tab" href="#users">Users</a>
                    </li>
                  </ul>

                  <div id="maintenance-tab-content" className="tab-content">
                    <div className="tab-pane fade active show" id="roles">
                      {this.state.roles.length > 0 ?
                        <div className="maintenance-table">
                          <Waffles<MaintenanceTableData>
                            columns={columnConfig}
                            data={this.createTableData(this.state.roles)}
                            disableProcessingOverlay={true}
                            fullWidth={true}
                            handleCheckboxChange={this.onRolesCheckboxChange}
                            selectedKeys={this.state.selectedRegisteredEntities.map((sre) => sre.registeredEntity)}
                            showCheckboxes={true}
                            testId="maintenance-admin-roles"
                          />
                        </div> :
                        <div className="text-center lead">There are no roles!</div>
                      }
                    </div>
                    <div className="tab-pane fade" id="users">
                      <UserSearch
                        clients={this.state.clients}
                        getUsers={this.getUsers}
                      />
                      {this.state.users.length > 0 &&
                        <div className="maintenance-table">
                          <Waffles<MaintenanceTableData>
                            columns={columnConfig}
                            data={this.createTableData(this.state.users)}
                            // disableProcessingOverlay={true}
                            fullWidth={true}
                            handleCheckboxChange={this.onUsersCheckboxChange}
                            selectedKeys={this.state.selectedRegisteredEntities.map((sre) => sre.registeredEntity)}
                            showCheckboxes={true}
                            testId="maintenance-admin-users"
                          />
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <QESButton
                    qesTestId="maintenance-admin_update-all"
                    className="float-end mt-3"
                    disabled={this.state.disableUpdateAllButton}
                    onClick={this.onUpdateAllClick}
                  >
                    Update All
                  </QESButton>
                </div>
              </div>
            </PageSection>
          }
        </PageInner>
      </Page>
    );
  }

  /********** creates the bulk edit's registered entity model  **********/
  private createBulkEditRegisteredEntityModel = () => {
    return {
      displayName: "BULK-EDIT",
      maintenanceTime: "",
      registeredEntity: "BULK-EDIT",
      registeredEntityType: MaintenanceRegisteredEntityType.Role,
      showMaintenanceAlert: false,
      showMaintenancePage: false,
    };
  }

  private createTableData = (source: MaintenanceRegisteredEntity[]) => {
    /* oxlint-disable sort-keys */
    return source.map<MaintenanceTableData>((item) => {
      return {
        [wafflesKeyColumnName]: item.registeredEntity,
        registeredEntity: (
          <div className="text-truncate" onClick={() => this.onSelectRegisteredEntityToggle(item)}>
            {item.displayName}
          </div>
        ),
        maintenancePage: (
          <span
            className={"tile tile-circle cursor-pointer bg-" + (item.showMaintenancePage ? "green" : "grey")}
            onClick={() => { this.onMaintenancePageToggle(item); }}
          >
            <i className={"fa fa-" + (item.showMaintenancePage ? "check" : "times")} />
          </span>
        ),
        maintenanceAlert: (
          <span
            className={"tile tile-circle cursor-pointer bg-" + (item.showMaintenanceAlert ? "green" : "grey")}
            onClick={() => this.onMaintenanceAlertToggle(item)}
          >
            <i className={"fa fa-" + (item.showMaintenanceAlert ? "check" : "times")} />
          </span>
        ),
        maintenanceCountdown: (
          <input
            className="form-control cursor-pointer"
            onChange={(e) => this.onMaintenanceAlertCountdownChange(e, item)}
            type="datetime-local"
            value={item.maintenanceTime}
          />
        ),
      }
    })
    /* oxlint-enable */
  }

  /********** gets the names of all the registered entities being bulk-edited **********/
  private getBulkEditRegisteredEntities = () => {
    let bulkEditRegisteredEntities = "";

    if (this.state.selectedRegisteredEntities.length > 0) {
      for (let i = 0; i < this.state.selectedRegisteredEntities.length; i++) {
        bulkEditRegisteredEntities += this.state.selectedRegisteredEntities[i].displayName + (this.state.selectedRegisteredEntities.length - 1 === i ? "" : ", ");
      }
    }

    return bulkEditRegisteredEntities;
  }

  /********** retrieves the editable maintenance registered entities from the back end **********/
  private getMaintenanceRegisteredEntities = async () => {
    const maintenanceAdminViewModel: MaintenanceAdminViewModel = await this.maintenanceService.getMaintenanceAdminViewModel();
    const storedRoles: MaintenanceRegisteredEntity[] = maintenanceAdminViewModel.registeredEntities;
    const clients: Client[] = maintenanceAdminViewModel.clients;

    // cloning the stored registered entities object in order to
    // differentiate the registered entity properties stored on the server
    // from the ones being modified in order to determine
    // the "update all" button's disabled state
    const roles: MaintenanceRegisteredEntity[] = JSON.parse(JSON.stringify(storedRoles));

    this.setState({
      clients,
      loading: false,
      roles,
      storedRoles,
    });
  }

  private onCheckboxChange = (selectedKeys: string[]) => {
    // find the difference
    let changedEntity: MaintenanceRegisteredEntity = null;
    // const selectedRoles = this.state.selectedRegisteredEntities.filter((sre) => sre.registeredEntityType === MaintenanceRegisteredEntityType.Role);
    if (selectedKeys.length > this.state.selectedRegisteredEntities.length) {
      // something was added, find it
      const addedKey = selectedKeys.find((sk) => !this.state.selectedRegisteredEntities.find((sr) => sr.registeredEntity === sk));
      changedEntity = (this.state.roles.concat(this.state.users)).find((role) => role.registeredEntity === addedKey);
    } else {
      // something was removed, find it
      changedEntity = this.state.selectedRegisteredEntities.find((sr) => !selectedKeys.includes(sr.registeredEntity));
    }

    this.onSelectRegisteredEntityToggle(changedEntity);
  }

  private onRolesCheckboxChange = (selectedKeys: string[], isBulk: boolean) => {
    if (isBulk) {
      this.onSelectAllRolesToggle();
    } else {
      this.onCheckboxChange(selectedKeys);
    }
  }

  private onUsersCheckboxChange = (selectedKeys: string[], isBulk: boolean) => {
    if (isBulk) {
      this.onSelectAllUsersToggle();
    } else {
      this.onCheckboxChange(selectedKeys);
    }
  }
}

export { MaintenanceAdmin };
